"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.List = void 0;
var tslib_1 = require("tslib");
var React = require("react");
var TypeStyle = require("typestyle");
var DomUtil_1 = require("../../utils/DomUtil");
var FindIndex_1 = require("../../utils/FindIndex");
var ReactHelpers_1 = require("../../utils/ReactHelpers");
var ListItemUtils_1 = require("./ListItemUtils");
var ListStyles_1 = require("./ListStyles");
/**
 * `List` displays a keyboard-navigable list of items similar to an HTML `<select multiple>`. However, unlike `<select multiple>`,
 * it only supports single-selection, as defined in the WAI-ARIA spec for the listbox role. List supports typeahead-focus as described
 * in that spec, and displays a background color on the selected item. Items are supplied via an array of `ListItem`, which has properties
 * similar to `<option>`.
 */
var List = /** @class */ (function (_super) {
    tslib_1.__extends(List, _super);
    function List() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        _this.listElement = null;
        _this.itemTextContent = [];
        _this.typeAheadQuery = '';
        _this.typeAheadTimeoutHandle = 0;
        _this.state = {
            activeItemIndex: Math.max(0, _this.props.selectedValue ? ListItemUtils_1.findIndexOfItemWithValue(_this.props.items, _this.props.selectedValue) : 0),
        };
        _this.handleFocus = ReactHelpers_1.mergeEventHandlers(function (e) {
            if (e.target !== e.currentTarget) {
                var closestOption = DomUtil_1.closest(e.target, '[role="option"]');
                if (!closestOption) {
                    return;
                }
                var activeItemIndex = parseInt(closestOption.getAttribute('data-itemindex'), 10);
                if (!isNaN(activeItemIndex) && activeItemIndex !== _this.state.activeItemIndex) {
                    _this.setState({ activeItemIndex: activeItemIndex });
                }
            }
        }, function (e) { var _a, _b; return (_b = (_a = _this.props).onFocus) === null || _b === void 0 ? void 0 : _b.call(_a, e); });
        _this.handleClick = ReactHelpers_1.mergeEventHandlers(function (e) {
            _this.handleItemSelection(e.target);
        }, function (e) { var _a, _b; return (_b = (_a = _this.props).onClick) === null || _b === void 0 ? void 0 : _b.call(_a, e); });
        _this.keyDownHandlers = Object.freeze({
            'ArrowUp': function () { return ListItemUtils_1.findFocusableItemIndex(_this.props.items, _this.state.activeItemIndex, -1); },
            'ArrowDown': function () { return ListItemUtils_1.findFocusableItemIndex(_this.props.items, _this.state.activeItemIndex, +1); },
            'Home': function () { return ListItemUtils_1.findFocusableItemIndex(_this.props.items, -1, 1); },
            'End': function () { return ListItemUtils_1.findFocusableItemIndex(_this.props.items, _this.props.items.length, -1); },
            ' ': function (e) {
                if (!e.repeat) {
                    _this.handleItemSelection(e.target);
                }
                return _this.state.activeItemIndex;
            },
            'Enter': function (e) {
                if (_this.props.selectOnEnterKey) {
                    _this.keyDownHandlers[' '](e);
                }
                return _this.state.activeItemIndex;
            },
        });
        _this.handleKeyDown = ReactHelpers_1.mergeEventHandlers(function (e) {
            if (_this.keyDownHandlers[e.key]) {
                e.stopPropagation();
                e.preventDefault();
                var activeItemIndex = _this.keyDownHandlers[e.key](e);
                if (activeItemIndex !== _this.state.activeItemIndex) {
                    _this.setState({ activeItemIndex: activeItemIndex });
                }
            }
        }, function (e) { var _a, _b; return (_b = (_a = _this.props).onKeyDown) === null || _b === void 0 ? void 0 : _b.call(_a, e); });
        _this.handleKeyPress = ReactHelpers_1.mergeEventHandlers(function (e) {
            if (e.key === ' ' || e.key === 'Enter' || e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) {
                return;
            }
            window.clearTimeout(_this.typeAheadTimeoutHandle);
            _this.typeAheadTimeoutHandle = window.setTimeout(_this.resetQuery, 500);
            _this.typeAheadQuery += e.key;
            var query = _this.typeAheadQuery.toLowerCase();
            var activeItemIndex = FindIndex_1.findIndex(_this.itemTextContent, function (text, itemIndex) {
                return ListItemUtils_1.isFocusableItem(_this.props.items[itemIndex]) && text.toLowerCase().indexOf(query) === 0;
            }, _this.state.activeItemIndex + (query.length === 1 ? 1 : 0));
            if (activeItemIndex >= 0) {
                _this.setState({ activeItemIndex: activeItemIndex });
            }
        }, function (e) { var _a, _b; return (_b = (_a = _this.props).onKeyPress) === null || _b === void 0 ? void 0 : _b.call(_a, e); });
        _this.handleListRef = function (ref) {
            _this.listElement = ref;
        };
        _this.resetQuery = function () {
            _this.typeAheadQuery = '';
        };
        return _this;
    }
    List.prototype.componentDidMount = function () {
        this.memoizeItemTextContent();
    };
    List.prototype.componentDidUpdate = function (prevProps, prevState) {
        var items = this.props.items;
        if (!items) {
            return;
        }
        if (prevProps.items !== items) {
            this.memoizeItemTextContent();
        }
        if (items.length && this.state.activeItemIndex >= items.length) {
            this.setState({ activeItemIndex: items.length - 1 });
            return;
        }
        if (prevState.activeItemIndex !== this.state.activeItemIndex) {
            this.syncFocus();
        }
    };
    List.prototype.handleItemSelection = function (target) {
        var itemNode = DomUtil_1.closest(target, '[role="option"]:not([aria-disabled="true"])');
        if (!this.props.onSelect || !itemNode) {
            return;
        }
        var value = itemNode.getAttribute('data-itemvalue');
        this.props.onSelect(value);
    };
    List.prototype.memoizeItemTextContent = function () {
        this.itemTextContent = [];
        var listNode = this.listElement;
        if (!listNode) {
            return;
        }
        var item;
        // tslint:disable-next-line:no-any
        for (var _i = 0, _a = listNode.children; _i < _a.length; _i++) {
            item = _a[_i];
            this.itemTextContent.push(item.textContent);
        }
    };
    List.prototype.syncFocus = function () {
        if (this.listElement && this.listElement.contains(document.activeElement)) {
            var activeItem = this.listElement.querySelector("[data-itemindex=\"" + this.state.activeItemIndex + "\"]");
            activeItem === null || activeItem === void 0 ? void 0 : activeItem.focus();
        }
    };
    List.prototype.renderItems = function (items, selectedValue, activeItemIndex) {
        var _this = this;
        if (!items) {
            return [];
        }
        var listId = this.props.id;
        var setsize = items.filter(ListItemUtils_1.isFocusableItem).length;
        var itemNodes = [];
        var _loop_1 = function (index, posinset) {
            var item = items[index];
            if (item.separator) {
                itemNodes.push(React.createElement("div", { "data-tb-test-id": item.testId, key: "separator%%" + index, role: 'separator' }));
            }
            else {
                if (!item.value) {
                    throw new Error("Item has no value: " + JSON.stringify(item));
                }
                var makeItemElem = function (listItem) {
                    // per option role spec, unselected items should be aria-selected=false if selectable, but undefined if not selectable
                    var isSelected = !!selectedValue && listItem.value === selectedValue ? true : (!!_this.props.onSelect ? false : undefined);
                    var tabIndex = index === activeItemIndex ? 0 : -1;
                    posinset++; // increment first as aria-posinset starts counting from 1
                    return ListItem({ index: index, isSelected: isSelected, item: listItem, listId: listId, posinset: posinset, setsize: setsize, tabIndex: tabIndex });
                };
                // this logic has much in common with similar logic in ListMenuWidget::buildMenuFromMenuItemInfos
                if (item.group) {
                    var groupLabelId = DomUtil_1.generateId();
                    var groupItemNodes = [];
                    for (index++; ListItemUtils_1.isFocusableItem(items[index]); index++) {
                        groupItemNodes.push(makeItemElem(items[index]));
                    }
                    index--;
                    itemNodes.push(React.createElement("div", { role: 'group', "aria-labelledby": groupLabelId, key: "group%%" + item.value },
                        React.createElement("div", { role: 'presentation', id: groupLabelId, "data-tb-test-id": item.testId, key: 'title' }, ListItemContent({ item: item })),
                        groupItemNodes));
                }
                else {
                    itemNodes.push(makeItemElem(item));
                }
            }
            out_index_1 = index;
            out_posinset_1 = posinset;
        };
        var out_index_1, out_posinset_1;
        for (var index = 0, posinset = 0; index < items.length; index++) {
            _loop_1(index, posinset);
            index = out_index_1;
            posinset = out_posinset_1;
        }
        return itemNodes;
    };
    List.prototype.render = function () {
        // tslint:disable-next-line:max-line-length
        var _a = this.props, className = _a.className, disableTabAccessibility = _a.disableTabAccessibility, items = _a.items, onClick = _a.onClick, onSelect = _a.onSelect, selectedValue = _a.selectedValue, selectOnEnterKey = _a.selectOnEnterKey, testId = _a.testId, divProps = tslib_1.__rest(_a, ["className", "disableTabAccessibility", "items", "onClick", "onSelect", "selectedValue", "selectOnEnterKey", "testId"]);
        return (React.createElement("div", tslib_1.__assign({}, ReactHelpers_1.EventUtilsOptOut, divProps, { className: TypeStyle.classes(ListStyles_1.ListStyleName, className), "data-tb-test-id": testId, onClick: this.handleClick, onFocus: this.handleFocus, onKeyDown: this.handleKeyDown, onKeyPress: this.handleKeyPress, ref: this.handleListRef, role: 'listbox' }), this.renderItems(items, selectedValue, disableTabAccessibility ? undefined : this.state.activeItemIndex)));
    };
    return List;
}(React.Component));
exports.List = List;
// tslint:disable-next-line: max-line-length
var ListItem = function (_a) {
    var index = _a.index, item = _a.item, isSelected = _a.isSelected, listId = _a.listId, posinset = _a.posinset, setsize = _a.setsize, tabIndex = _a.tabIndex;
    return React.createElement("div", { "aria-disabled": item.disabled, "aria-posinset": posinset, "aria-selected": isSelected, "aria-setsize": setsize, "data-itemindex": index, "data-itemvalue": item.value, "data-tb-test-id": item.testId, key: "item%%" + item.value, id: listId ? listId + "-" + index : undefined, role: 'option', tabIndex: tabIndex, title: item.tooltip }, ListItemContent({ item: item }));
};
var ListItemContent = function (_a) {
    var item = _a.item;
    return React.createElement("span", { className: ListStyles_1.EllipsedStyleName }, item.content || item.value);
};
//# sourceMappingURL=List.js.map